/**
 * Edit-theater animation runner — plays the server's semantic patches
 * against the live draft: highlight (amber wash), delete (strikethrough,
 * beat, collapse), type (caret types chunk-by-chunk), replace (highlight →
 * strike → type), verify (green underline flash).
 *
 * Also paces the accuracy checklist: every act the server declares gets a
 * visible row that checks off in sync with its ops — unhurried, one at a
 * time — and acts with nothing to do flip to "skipped" rather than being
 * omitted. When the work is done the checklist plays its outro
 * (shrink/fade) before the receipt lands and the typeset takes over.
 *
 * Guardrails (brain/plan/V6-BIG-BASE.md): total show budget ~6.5s — when
 * the ops outrun it the rest apply instantly; prefers-reduced-motion
 * collapses everything to instant application; the show can never corrupt
 * the commentary (the complete body replaces the draft regardless).
 */

import { useEffect, useRef, useState } from 'react';
import type { ChatMessage, EditCheck, EditTheaterOp } from '@state/chat';

export interface Decoration {
  start: number;
  end: number;
  cls: string;
}

export interface ActState {
  act: string;
  label: string;
  status: 'pending' | 'running' | 'done' | 'skipped';
}

/** The canonical scripture-accuracy steps, in performance order. Labels are
 *  the resting (imperative) form; server check events refine them. */
export const ACCURACY_ACTS: ReadonlyArray<{ act: string; label: string }> = [
  { act: 'quotes', label: 'Check quotes against the KJV' },
  { act: 'lexicon', label: "Verify original languages against Strong's" },
  { act: 'cross-refs', label: 'Add verified cross-references' },
  { act: 'scripture', label: 'Confirm references against the KJV' },
];

// UAT 2026-07-05: Brian asked for slower, more legible repairs — the show
// budget and per-op timings are deliberately unhurried (Skip stays).
const SHOW_BUDGET_MS = 12_000;
const RUNNER_TIMEOUT_MS = 20_000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useEditTheater(message: ChatMessage, onTheaterDone: () => void) {
  const [doc, setDoc] = useState('');
  const [marks, setMarks] = useState<Decoration[]>([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [actStates, setActStates] = useState<ActState[]>(
    ACCURACY_ACTS.map((a) => ({ ...a, status: 'pending' })),
  );
  const [checklistCollapsed, setChecklistCollapsed] = useState(false);

  const opsRef = useRef<EditTheaterOp[]>([]);
  opsRef.current = message.editOps ?? [];
  const checksRef = useRef<EditCheck[]>([]);
  checksRef.current = message.editChecks ?? [];
  const receiptRef = useRef(false);
  receiptRef.current = message.editReceipt != null;
  const doneRef = useRef(onTheaterDone);
  doneRef.current = onTheaterDone;
  const skipRef = useRef(false);
  skipRef.current = skipped;

  const startedRef = useRef(false);
  const docRef = useRef('');

  useEffect(() => {
    if (!message.editBaseline || startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    docRef.current = message.editBaseline;
    setDoc(message.editBaseline);

    const setText = (text: string) => {
      docRef.current = text;
      if (!cancelled) setDoc(text);
    };
    const mark = (m: Decoration | null) => {
      if (!cancelled) setMarks(m ? [m] : []);
    };
    const instant = () => reduced || skipRef.current;
    const wait = async (ms: number, budget: { left: number }) => {
      if (instant() || budget.left <= 0) return;
      const t = Math.min(ms, budget.left);
      budget.left -= t;
      await sleep(t);
    };
    // Checklist beats sit outside the op budget — the check-offs stay
    // legible even when a dense show burns the op budget down.
    const beat = async (ms: number) => {
      if (!instant()) await sleep(ms);
    };

    // ── Checklist pacing ──────────────────────────────────────────────
    const finalized = new Set<string>();
    let currentAct: string | null = null;

    const patchAct = (act: string, patch: Partial<ActState>) => {
      if (cancelled) return;
      setActStates((prev) => {
        const idx = prev.findIndex((a) => a.act === act);
        if (idx === -1) {
          return [...prev, { act, label: act, status: 'pending', ...patch } as ActState];
        }
        return prev.map((a, i) => (i === idx ? { ...a, ...patch } : a));
      });
    };

    const actOrder = (): string[] => {
      const order = ACCURACY_ACTS.map((a) => a.act);
      for (const c of checksRef.current) if (!order.includes(c.act)) order.push(c.act);
      return order;
    };

    /** Finalize every act declared before `target` (all when null): done
     *  acts get their finished label, no-op acts flip to "skipped" — each
     *  with a visible beat so the check-offs never blur past the reader. */
    const advanceThrough = async (target: string | null) => {
      const order = actOrder();
      const stop = target == null ? order.length : Math.max(0, order.indexOf(target));
      for (let i = 0; i < stop; i++) {
        const act = order[i];
        if (finalized.has(act)) continue;
        finalized.add(act);
        const check = checksRef.current.find((c) => c.act === act);
        if (!check || check.status === 'skipped') {
          patchAct(act, { status: 'running', ...(check && { label: check.label }) });
          await beat(280);
          patchAct(act, { status: 'skipped' });
          await beat(140);
        } else {
          patchAct(act, { status: 'done', label: check.label });
          await beat(220);
        }
      }
    };

    const typeText = async (start: number, text: string, budget: { left: number }) => {
      if (instant() || budget.left <= 0) {
        setText(docRef.current.slice(0, start) + text + docRef.current.slice(start));
        return;
      }
      // Caret types 2–4 chars per frame — an editor's hand, not another LLM.
      const total = Math.min(120 + text.length * 14, 1_800, budget.left);
      const chunk = Math.max(2, Math.ceil(text.length / Math.max(1, total / 42)));
      let typed = 0;
      const base = docRef.current;
      while (typed < text.length) {
        typed = Math.min(text.length, typed + chunk);
        setText(base.slice(0, start) + text.slice(0, typed) + base.slice(start));
        mark({ start, end: start + typed, cls: 'edit-mark--typed' });
        await sleep(42);
        if (instant()) {
          setText(base.slice(0, start) + text + base.slice(start));
          break;
        }
      }
      budget.left -= total;
    };

    const applyOp = async (op: EditTheaterOp, budget: { left: number }) => {
      const end = op.end ?? op.start;
      if (op.op === 'verify') {
        mark({ start: op.start, end, cls: 'edit-mark--verify' });
        await wait(500, budget);
        mark(null);
        return;
      }
      if (op.op === 'highlight') {
        mark({ start: op.start, end, cls: 'edit-mark--highlight' });
        await wait(900, budget);
        return;
      }
      if (op.op === 'delete') {
        mark({ start: op.start, end, cls: 'edit-mark--strike' });
        await wait(700, budget);
        setText(docRef.current.slice(0, op.start) + docRef.current.slice(end));
        mark(null);
        await wait(200, budget);
        return;
      }
      if (op.op === 'replace') {
        mark({ start: op.start, end, cls: 'edit-mark--highlight' });
        await wait(600, budget);
        mark({ start: op.start, end, cls: 'edit-mark--strike' });
        await wait(500, budget);
        setText(docRef.current.slice(0, op.start) + docRef.current.slice(end));
        await typeText(op.start, op.text ?? '', budget);
        mark(null);
        return;
      }
      if (op.op === 'type') {
        await typeText(op.start, op.text ?? '', budget);
        mark(null);
      }
    };

    const run = async () => {
      const budget = { left: SHOW_BUDGET_MS };
      const t0 = Date.now();
      let applied = 0;
      for (;;) {
        if (cancelled) return;
        if (applied < opsRef.current.length) {
          const op = opsRef.current[applied];
          // Entering a new act: settle every earlier row first, then light
          // this one up before its patches perform.
          if (op.act && op.act !== currentAct) {
            await advanceThrough(op.act);
            patchAct(op.act, { status: 'running' });
            currentAct = op.act;
            await beat(250);
          }
          await applyOp(op, budget);
          applied += 1;
          continue;
        }
        // Ops drained — the receipt is the curtain call.
        if (receiptRef.current) break;
        if (Date.now() - t0 > RUNNER_TIMEOUT_MS) break;
        await sleep(60);
      }
      if (cancelled) return;
      // Settle the tail of the checklist (trailing skipped/done rows) …
      await advanceThrough(null);
      // … let the finished checklist breathe so the reader can take it in
      // (UAT 2026-08-13: 3s), then fold it up accordion-style — it stays
      // reopenable, never fades away.
      await beat(3_000);
      setChecklistCollapsed(true);
      await beat(500);
      setShowReceipt(true);
      if (!instant()) await sleep(400);
      if (!cancelled) doneRef.current();
    };

    void run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.editBaseline]);

  return {
    doc,
    marks,
    showReceipt,
    skip: () => setSkipped(true),
    skipped,
    actStates,
    checklistCollapsed,
  };
}
